import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import store from "src/page/store";
import { immediateToast } from "izitoast-react";
import { refreshSession } from "src/utils";
import { getConfig, AppConfig } from "src/config/Config";
import { logoutAction } from "src/page/auth/loginSlice";
import {
  trackEvent,
  API_ERROR_EVENTS,
  parseGraphQLOperation,
  trackGraphQLOperation,
  enrichEventProperties
} from "src/services/analytics";

let internalApiInstance: AxiosInstance | null = null;

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let isLoggedOut = false;
// Dedupe the access-denied toast: a page often fires several denied ops at once.
let lastAccessDeniedAt = 0;

export function resetInternalLoggedOutState() {
  isLoggedOut = false;
  isRefreshing = false;
  refreshPromise = null;
}

export function getInternalClient(): AxiosInstance {
  if (internalApiInstance) {
    return internalApiInstance;
  }

  const config: AppConfig = getConfig();

  const api = axios.create({
    baseURL: config.INTERNAL_API_KEY,
    headers: {
      "Content-Type": "application/json",
      partner_id: config.PARTNER_ID
    }
  });

  api.interceptors.request.use(async (configuration: InternalAxiosRequestConfig) => {
    if (isLoggedOut) {
      return Promise.reject(new Error("Session expired. User logged out."));
    }
    try {
      const {
        auth: { cognitoIdToken }
      } = store.getState();

      if (cognitoIdToken) {
        configuration.headers = configuration.headers || {};
        configuration.headers["Authorization"] = `Bearer ${cognitoIdToken}`;
      }
    } catch (error) {
      console.error(error);
    }
    return configuration;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config.url?.includes("/graphql") && response.config.data) {
        try {
          const requestData =
            typeof response.config.data === "string" ? JSON.parse(response.config.data) : response.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const hasErrors = !!response.data.errors;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: !hasErrors,
              requestPayload: requestData.variables,
              responseData: response.data,
              ...(hasErrors && {
                errorMessage: response.data.errors[0]?.message,
                errorType: response.data.errors[0]?.errorType
              })
            });
          }
        } catch (error) {
          console.error("Failed to track GraphQL operation:", error);
        }
      }

      if (response.data.errors) {
        const errorMessage = response.data.errors[0].message;
        let messageObj: { message?: string } | undefined;

        try {
          messageObj = JSON.parse(errorMessage);
        } catch (error) {
          console.log(error);
          messageObj = undefined;
        }

        immediateToast("error", {
          message: messageObj?.message ?? errorMessage,
          timeout: 3000,
          position: "topCenter"
        });
        throw new Error(response.data.errors[0]);
      }
      return response.data;
    },
    async function (error: any) {
      if (error.config?.url?.includes("/graphql") && error.config.data) {
        try {
          const requestData = typeof error.config.data === "string" ? JSON.parse(error.config.data) : error.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const errorMessage = error?.response?.data?.errors?.[0]?.message || error.message || "Unknown error";
            const errorType = error?.response?.data?.errors?.[0]?.errorType || error.constructor.name;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: false,
              errorMessage,
              errorType,
              requestPayload: requestData.variables,
              responseData: error?.response?.data
            });
          }
        } catch (trackingError) {
          console.error("Failed to track GraphQL error:", trackingError);
        }
      }

      const originalRequest: InternalAxiosRequestConfig = error.config;
      const {
        auth: { cognitoRefreshToken }
      } = store.getState();
      const dispatch = store.dispatch;

      if (error.response?.status === 401) {
        if (isLoggedOut) {
          return Promise.reject(new Error("Session expired. User logged out."));
        }
        const originalRequestWithRetry = originalRequest as InternalAxiosRequestConfig & { _retry?: number };

        if (!originalRequestWithRetry._retry) originalRequestWithRetry._retry = 0;

        if (originalRequestWithRetry._retry < 1) {
          originalRequestWithRetry._retry += 1;

          // Track retry attempt
          const retryProperties = enrichEventProperties({
            endpoint: originalRequest.url || "unknown",
            method: originalRequest.method?.toUpperCase() || "unknown",
            retry_count: originalRequestWithRetry._retry
          });

          trackEvent(API_ERROR_EVENTS.API_RETRY_ATTEMPTED, retryProperties);

          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                const session = await refreshSession(cognitoRefreshToken ?? undefined);

                isRefreshing = false;
                refreshPromise = null;
                return session;
              } catch (err) {
                isRefreshing = false;
                refreshPromise = null;
                isLoggedOut = true;
                // Track token refresh failure
                const refreshErrorProperties = enrichEventProperties({
                  error_message: err instanceof Error ? err.message : "Token refresh failed"
                });

                trackEvent(API_ERROR_EVENTS.TOKEN_REFRESH_FAILED, refreshErrorProperties);
                dispatch(logoutAction());
                return Promise.reject(new Error("Session expired. User logged out."));
              }
            })();
          }

          try {
            const session = await refreshPromise;

            if (originalRequestWithRetry.headers) {
              originalRequestWithRetry.headers["Authorization"] =
                `Bearer ${(session as unknown as { idToken: string }).idToken}`;
            }
            return api(originalRequestWithRetry);
          } catch (err) {
            return Promise.reject(new Error("Session expired. User logged out."));
          }
        } else {
          // Refreshed once and STILL 401 → a valid session repeatedly rejected is
          // an authorization DENY from the AppSync authorizer (e.g. a Read-Only
          // Admin hitting an action they aren't granted), NOT an expired session.
          // Show "access denied" and DO NOT log out; only a genuine session failure
          // (refresh threw above) reaches logout.
          const errorType = error.response?.data?.errors?.[0]?.errorType;

          if (errorType === "UnauthorizedException") {
            const now = Date.now();
            if (now - lastAccessDeniedAt > 2000) {
              lastAccessDeniedAt = now;
              immediateToast("error", {
                message: "You don't have permission to do this.",
                timeout: 3000,
                position: "topCenter"
              });
            }
            return Promise.reject(new Error("Access denied"));
          }

          isLoggedOut = true;
          dispatch(logoutAction());
          return Promise.reject(new Error("Session expired. User logged out."));
        }
      } else {
        immediateToast("error", {
          message: error.message || "An unexpected error occurred",
          timeout: 3000,
          position: "topCenter"
        });
        return Promise.reject(error);
      }
    }
  );

  internalApiInstance = api;
  return internalApiInstance;
}

const internalClient = (data: { query: string; variables?: any }) => {
  return getInternalClient().post("/graphql", data);
};

export default internalClient;
